import { useEffect, useMemo, useRef, useState } from 'react';
import {
  collection, onSnapshot, addDoc, updateDoc, deleteDoc,
  doc, serverTimestamp, query, orderBy,
} from 'firebase/firestore';
import { ref as storageRef, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import { PORTAL_KEYS, PORTAL_META } from '../../contexts/MarketContext';

// Preset categories, shared with the per-tour resource pack.
export const RESOURCE_CATEGORIES = [
  'Risk Assessments',
  'Provider Forms',
  'Guidance',
  'Certificates',
  'Checklists',
  'Other',
];

const MAX_FILE_BYTES = 20 * 1024 * 1024;

export function formatBytes(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Expiry state for a resource. Certificates (public liability, DBS, hotel fire
 * certs) go stale on a fixed date — surfacing that here is what stops last
 * season's certificate being emailed to a school.
 */
export function expiryState(expiryDate) {
  if (!expiryDate) return null;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const exp = new Date(`${expiryDate}T00:00:00`);
  if (Number.isNaN(exp.getTime())) return null;
  const days = Math.round((exp - today) / 86400000);
  if (days < 0) return { level: 'expired', days, label: 'Expired' };
  if (days <= 60) return { level: 'soon', days, label: `Expires in ${days} day${days === 1 ? '' : 's'}` };
  return { level: 'ok', days, label: `Valid to ${formatDateShort(expiryDate)}` };
}

export function formatDateShort(str) {
  if (!str) return '';
  const [y, m, d] = str.split('-');
  return `${d}/${m}/${y}`;
}

const emptyForm = {
  title: '',
  category: '',
  description: '',
  version: '',
  reviewedDate: '',
  expiryDate: '',
  portals: [],
  defaultInclude: false,
};

function formFromResource(r) {
  return {
    title: r.title || '',
    category: r.category || '',
    description: r.description || '',
    version: r.version || '',
    reviewedDate: r.reviewedDate || '',
    expiryDate: r.expiryDate || '',
    portals: Array.isArray(r.portals) ? r.portals : [],
    defaultInclude: !!r.defaultInclude,
  };
}

function fileFields(form, file, downloadUrl, path) {
  return {
    [`${form}Url`]: downloadUrl,
    [`${form}Path`]: path,
    [`${form}Name`]: file.name,
    [`${form}Size`]: file.size,
    [`${form}UploadedAt`]: new Date().toISOString(),
  };
}

// Toggling a portal on a form's `portals` array. Defined at module level (like
// the two form components below) so typing in a field doesn't remount inputs.
function togglePortal(setState, key) {
  setState((prev) => ({
    ...prev,
    portals: prev.portals.includes(key)
      ? prev.portals.filter((p) => p !== key)
      : [...prev.portals, key],
  }));
}

function PortalPicker({ value, onToggle }) {
  return (
    <div className="flex flex-wrap gap-2">
      {PORTAL_KEYS.map((k) => {
        const on = value.includes(k);
        return (
          <button
            key={k}
            type="button"
            onClick={() => onToggle(k)}
            className={`px-[11px] py-[6px] rounded-[8px] text-[12px] font-bold border transition ${
              on ? 'bg-pb text-white border-pb' : 'bg-white text-[#6B7280] border-[#E5E7EB] hover:border-pb/40'
            }`}
          >
            {PORTAL_META[k].label}
          </button>
        );
      })}
      {value.length === 0 && (
        <span className="text-[11.5px] text-[#9CA3AF] self-center">None selected = shows on every portal</span>
      )}
    </div>
  );
}

function ResourceFields({ state, setState }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <div className="sm:col-span-2">
        <label className="block text-[13px] font-medium text-[#374151] mb-1">
          Title <span className="text-dr">*</span>
        </label>
        <input
          type="text"
          value={state.title}
          onChange={(e) => setState((p) => ({ ...p, title: e.target.value }))}
          placeholder="e.g. Travelling by Coach — Risk Assessment"
          className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-pb"
        />
      </div>
      <div>
        <label className="block text-[13px] font-medium text-[#374151] mb-1">Category</label>
        <select
          value={state.category}
          onChange={(e) => setState((p) => ({ ...p, category: e.target.value }))}
          className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-pb bg-white"
        >
          <option value="">Select a category…</option>
          {RESOURCE_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>
      <div>
        <label className="block text-[13px] font-medium text-[#374151] mb-1">Version / reference</label>
        <input
          type="text"
          value={state.version}
          onChange={(e) => setState((p) => ({ ...p, version: e.target.value }))}
          placeholder="e.g. 2026-27 or v3"
          className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-pb"
        />
      </div>
      <div>
        <label className="block text-[13px] font-medium text-[#374151] mb-1">Last reviewed</label>
        <input
          type="date"
          value={state.reviewedDate}
          onChange={(e) => setState((p) => ({ ...p, reviewedDate: e.target.value }))}
          className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-pb"
        />
      </div>
      <div>
        <label className="block text-[13px] font-medium text-[#374151] mb-1">Expires</label>
        <input
          type="date"
          value={state.expiryDate}
          onChange={(e) => setState((p) => ({ ...p, expiryDate: e.target.value }))}
          className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-pb"
        />
        <p className="text-[11px] text-[#9CA3AF] mt-1">Certificates only — flags before it lapses.</p>
      </div>
      <div className="sm:col-span-2">
        <label className="block text-[13px] font-medium text-[#374151] mb-1">Description</label>
        <input
          type="text"
          value={state.description}
          onChange={(e) => setState((p) => ({ ...p, description: e.target.value }))}
          placeholder="Optional — a line to help staff pick the right one"
          className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-pb"
        />
      </div>
      <div className="sm:col-span-2">
        <label className="block text-[13px] font-medium text-[#374151] mb-2">Show on which portals</label>
        <PortalPicker value={state.portals} onToggle={(k) => togglePortal(setState, k)} />
      </div>
      <div className="sm:col-span-2">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={state.defaultInclude}
            onChange={(e) => setState((p) => ({ ...p, defaultInclude: e.target.checked }))}
            className="w-4 h-4 rounded border-[#D1D5DB] text-pb cursor-pointer"
          />
          <span className="text-[13px] text-[#374151]">Tick by default on every tour's resource pack</span>
        </label>
      </div>
    </div>
  );
}

function FormatChip({ label, url, size, tone }) {
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      title={size ? `${label} · ${formatBytes(size)}` : label}
      className={`inline-flex items-center gap-1 px-[8px] py-[3px] rounded-[6px] text-[10.5px] font-extrabold transition ${
        tone === 'pdf'
          ? 'bg-red-light text-dr hover:bg-[#FBD5D5]'
          : 'bg-blue-light text-pb hover:bg-[#DCE7F5]'
      }`}
    >
      {label}
    </a>
  );
}

export default function Resources() {
  const { currentUser } = useAuth();
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [deletingId, setDeletingId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState(emptyForm);
  const [search, setSearch] = useState('');
  const [uploadingKey, setUploadingKey] = useState(null);

  const newPdfRef = useRef(null);
  const newDocxRef = useRef(null);

  useEffect(() => {
    const q = query(collection(db, 'resources'), orderBy('title', 'asc'));
    const unsub = onSnapshot(q, (snap) => {
      setResources(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });
    return unsub;
  }, []);

  async function uploadResourceFile(resourceId, format, file) {
    const safeName = file.name.replace(/[^\w.\-() ]+/g, '_');
    const path = `resources/${resourceId}/${format}-${Date.now()}-${safeName}`;
    const sRef = storageRef(storage, path);
    await uploadBytes(sRef, file, { contentType: file.type || 'application/octet-stream' });
    const downloadUrl = await getDownloadURL(sRef);
    return { downloadUrl, path };
  }

  async function handleSave(e) {
    e.preventDefault();
    setError('');
    if (!form.title.trim()) { setError('Title is required.'); return; }

    const pdfFile = newPdfRef.current?.files?.[0] || null;
    const docxFile = newDocxRef.current?.files?.[0] || null;
    if (!pdfFile && !docxFile) { setError('Attach at least one file (PDF and/or Word).'); return; }
    for (const f of [pdfFile, docxFile]) {
      if (f && f.size > MAX_FILE_BYTES) { setError(`"${f.name}" is over 20MB.`); return; }
    }

    setSaving(true);
    try {
      const created = await addDoc(collection(db, 'resources'), {
        title: form.title.trim(),
        category: form.category,
        description: form.description.trim(),
        version: form.version.trim(),
        reviewedDate: form.reviewedDate,
        expiryDate: form.expiryDate,
        portals: form.portals,
        defaultInclude: form.defaultInclude,
        createdAt: serverTimestamp(),
        createdBy: currentUser?.email || '',
      });

      const fileUpdate = {};
      if (pdfFile) {
        const { downloadUrl, path } = await uploadResourceFile(created.id, 'pdf', pdfFile);
        Object.assign(fileUpdate, fileFields('pdf', pdfFile, downloadUrl, path));
      }
      if (docxFile) {
        const { downloadUrl, path } = await uploadResourceFile(created.id, 'docx', docxFile);
        Object.assign(fileUpdate, fileFields('docx', docxFile, downloadUrl, path));
      }
      if (Object.keys(fileUpdate).length) await updateDoc(created, fileUpdate);

      setForm(emptyForm);
      if (newPdfRef.current) newPdfRef.current.value = '';
      if (newDocxRef.current) newDocxRef.current.value = '';
      setShowForm(false);
    } catch (err) {
      console.error(err);
      setError('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  function startEdit(resource) {
    setEditingId(resource.id);
    setEditForm(formFromResource(resource));
    setError('');
  }

  async function handleUpdate(e) {
    e.preventDefault();
    if (!editForm.title.trim()) { setError('Title is required.'); return; }
    setError('');
    setSaving(true);
    try {
      await updateDoc(doc(db, 'resources', editingId), {
        title: editForm.title.trim(),
        category: editForm.category,
        description: editForm.description.trim(),
        version: editForm.version.trim(),
        reviewedDate: editForm.reviewedDate,
        expiryDate: editForm.expiryDate,
        portals: editForm.portals,
        defaultInclude: editForm.defaultInclude,
      });
      setEditingId(null);
    } catch (err) {
      console.error(err);
      setError('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  /** Replace (or add) one of the two file formats on an existing resource. */
  async function handleReplaceFile(resource, format, e) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > MAX_FILE_BYTES) { setError(`"${file.name}" is over 20MB.`); return; }
    setError('');
    setUploadingKey(`${resource.id}-${format}`);
    try {
      const oldPath = resource[`${format}Path`];
      const { downloadUrl, path } = await uploadResourceFile(resource.id, format, file);
      await updateDoc(doc(db, 'resources', resource.id), fileFields(format, file, downloadUrl, path));
      if (oldPath && oldPath !== path) {
        try { await deleteObject(storageRef(storage, oldPath)); } catch { /* already gone */ }
      }
    } catch (err) {
      console.error(err);
      setError('Upload failed. Please try again.');
    } finally {
      setUploadingKey(null);
    }
  }

  async function handleRemoveFile(resource, format) {
    const other = format === 'pdf' ? 'docx' : 'pdf';
    if (!resource[`${other}Url`]) {
      alert('A resource needs at least one file — upload the other format first.');
      return;
    }
    if (!window.confirm(`Remove the ${format === 'pdf' ? 'PDF' : 'Word'} version of "${resource.title}"?`)) return;
    try {
      if (resource[`${format}Path`]) {
        try { await deleteObject(storageRef(storage, resource[`${format}Path`])); } catch { /* already gone */ }
      }
      await updateDoc(doc(db, 'resources', resource.id), {
        [`${format}Url`]: null, [`${format}Path`]: null,
        [`${format}Name`]: null, [`${format}Size`]: null, [`${format}UploadedAt`]: null,
      });
    } catch (err) {
      console.error(err);
      setError('Could not remove that file. Please try again.');
    }
  }

  async function handleDelete(resource) {
    if (!window.confirm(`Remove "${resource.title}" from the library? Tours it has already been sent to keep their record.`)) return;
    setDeletingId(resource.id);
    try {
      for (const format of ['pdf', 'docx']) {
        const path = resource[`${format}Path`];
        if (path) { try { await deleteObject(storageRef(storage, path)); } catch { /* already gone */ } }
      }
      await deleteDoc(doc(db, 'resources', resource.id));
    } finally {
      setDeletingId(null);
    }
  }

  const filtered = useMemo(() => {
    if (!search) return resources;
    const s = search.toLowerCase();
    return resources.filter((r) =>
      [r.title, r.category, r.description, r.version].filter(Boolean).some((v) => v.toLowerCase().includes(s))
    );
  }, [resources, search]);

  // Grouped by category, categories in preset order with unknowns last.
  const grouped = useMemo(() => {
    const map = new Map();
    for (const r of filtered) {
      const key = r.category || 'Other';
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(r);
    }
    const ordered = [...RESOURCE_CATEGORIES, ...[...map.keys()].filter((k) => !RESOURCE_CATEGORIES.includes(k))];
    return ordered.filter((c) => map.has(c)).map((c) => [c, map.get(c)]);
  }, [filtered]);

  const expiringCount = resources.filter((r) => {
    const st = expiryState(r.expiryDate);
    return st && (st.level === 'expired' || st.level === 'soon');
  }).length;

  return (
    <div className="space-y-5">
      {/* ── Page header ── */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-[23px] font-extrabold text-navy tracking-[-0.4px]">Resources</h1>
          <p className="text-[13px] text-[#6B7280] mt-1">
            {resources.length > 0
              ? `${resources.length} document${resources.length !== 1 ? 's' : ''} — risk assessments, certificates and forms staff can send from any tour`
              : 'Your library of risk assessments, certificates, forms and guidance to send to schools and groups.'}
          </p>
        </div>
        {!showForm && (
          <button
            onClick={() => { setShowForm(true); setError(''); }}
            className="flex items-center gap-1.5 bg-pb hover:bg-navy text-white text-[13px] font-bold px-[16px] py-[10px] rounded-[9px] transition shadow-sm whitespace-nowrap"
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
              <path d="M12 5v14M5 12h14" />
            </svg>
            Add resource
          </button>
        )}
      </div>

      {expiringCount > 0 && (
        <div className="bg-[#FEF3C7] border border-[#FCD34D] rounded-[12px] px-[16px] py-[11px]">
          <p className="text-[12.5px] font-semibold text-[#92400E]">
            {expiringCount} resource{expiringCount !== 1 ? 's have' : ' has'} expired or expires within 60 days — check the certificates below before sending them out.
          </p>
        </div>
      )}

      {/* Add form */}
      {showForm && (
        <div className="bg-white rounded-[14px] border border-[#E5E7EB] shadow-[0_1px_2px_rgba(16,24,40,.04)] p-[18px]">
          <h3 className="text-[14.5px] font-bold text-[#111827] mb-5">New Resource</h3>
          <form onSubmit={handleSave} className="space-y-5">
            <ResourceFields state={form} setState={setForm} />

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-[13px] font-medium text-[#374151] mb-1">PDF version</label>
                <input
                  ref={newPdfRef}
                  type="file"
                  accept=".pdf,application/pdf"
                  className="w-full text-[12.5px] text-[#6B7280] file:mr-3 file:px-3 file:py-[7px] file:rounded-[8px] file:border-0 file:text-[12.5px] file:font-bold file:bg-blue-light file:text-pb hover:file:bg-[#DCE7F5]"
                />
                <p className="text-[11px] text-[#9CA3AF] mt-1">What schools normally file with their EVC.</p>
              </div>
              <div>
                <label className="block text-[13px] font-medium text-[#374151] mb-1">Word version</label>
                <input
                  ref={newDocxRef}
                  type="file"
                  accept=".doc,.docx"
                  className="w-full text-[12.5px] text-[#6B7280] file:mr-3 file:px-3 file:py-[7px] file:rounded-[8px] file:border-0 file:text-[12.5px] file:font-bold file:bg-blue-light file:text-pb hover:file:bg-[#DCE7F5]"
                />
                <p className="text-[11px] text-[#9CA3AF] mt-1">For groups who adapt it into their own paperwork.</p>
              </div>
            </div>

            {error && <p className="text-[12px] text-dr">{error}</p>}

            <div className="flex gap-2">
              <button type="submit" disabled={saving}
                className="px-[18px] py-[10px] text-[13px] font-bold text-white bg-pb hover:bg-navy rounded-[9px] transition disabled:opacity-60">
                {saving ? 'Saving…' : 'Save resource'}
              </button>
              <button type="button" onClick={() => { setShowForm(false); setForm(emptyForm); setError(''); }}
                className="px-[18px] py-[10px] text-[13px] font-semibold text-[#6B7280] hover:text-[#374151] transition">
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Search */}
      {!loading && resources.length > 0 && (
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search resources by title, category or version…"
          className="w-full sm:max-w-[360px] border border-[#E5E7EB] rounded-[9px] px-3 py-[9px] text-[13px] focus:outline-none focus:ring-2 focus:ring-pb"
        />
      )}

      {/* List */}
      {loading ? (
        <div className="bg-white rounded-[14px] border border-[#E5E7EB] p-8 text-center">
          <p className="text-[13px] text-[#9CA3AF]">Loading…</p>
        </div>
      ) : resources.length === 0 ? (
        <div className="bg-white rounded-[14px] border border-[#E5E7EB] shadow-[0_1px_2px_rgba(16,24,40,.04)] p-12 text-center">
          <p className="text-[14px] font-bold text-[#111827] mb-1">No resources yet</p>
          <p className="text-[13px] text-[#9CA3AF]">Add your risk assessments, certificates and forms above — they become tickable on every tour.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {grouped.map(([category, items]) => (
            <div key={category} className="bg-white rounded-[14px] border border-[#E5E7EB] shadow-[0_1px_2px_rgba(16,24,40,.04)] overflow-hidden">
              <div className="flex items-center justify-between px-[18px] py-[13px] border-b border-[#F1F3F6] bg-surface">
                <h3 className="text-[13px] font-extrabold text-[#111827]">{category}</h3>
                <span className="text-[11.5px] text-[#9CA3AF]">{items.length}</span>
              </div>
              <div className="divide-y divide-[#F1F3F6]">
                {items.map((r) => {
                  const exp = expiryState(r.expiryDate);
                  return (
                    <div key={r.id}>
                      <div className="flex flex-wrap items-start gap-3 px-[18px] py-[13px]">
                        <div className="flex-1 min-w-[220px]">
                          <div className="flex flex-wrap items-center gap-2">
                            <p className="text-[13px] font-bold text-[#111827]">{r.title}</p>
                            {r.version && (
                              <span className="text-[10.5px] font-bold bg-surface text-[#6B7280] px-[7px] py-[2px] rounded-[5px]">{r.version}</span>
                            )}
                            {r.defaultInclude && (
                              <span className="text-[10.5px] font-bold bg-green-light text-sg px-[7px] py-[2px] rounded-[5px]">Default</span>
                            )}
                            {exp && exp.level !== 'ok' && (
                              <span className={`text-[10.5px] font-bold px-[7px] py-[2px] rounded-[5px] ${
                                exp.level === 'expired' ? 'bg-red-light text-dr' : 'bg-[#FEF3C7] text-[#92400E]'
                              }`}>
                                {exp.label}
                              </span>
                            )}
                          </div>
                          {r.description && <p className="text-[12px] text-[#6B7280] mt-1">{r.description}</p>}
                          <p className="text-[11px] text-[#9CA3AF] mt-1">
                            {r.reviewedDate ? `Reviewed ${formatDateShort(r.reviewedDate)}` : 'No review date'}
                            {exp && exp.level === 'ok' && ` · ${exp.label}`}
                            {' · '}
                            {(!r.portals || r.portals.length === 0)
                              ? 'All portals'
                              : r.portals.map((p) => PORTAL_META[p]?.label || p).join(', ')}
                          </p>
                        </div>

                        <div className="flex items-center gap-1.5 flex-shrink-0">
                          {r.pdfUrl && <FormatChip label="PDF" url={r.pdfUrl} size={r.pdfSize} tone="pdf" />}
                          {r.docxUrl && <FormatChip label="WORD" url={r.docxUrl} size={r.docxSize} tone="doc" />}
                        </div>

                        <div className="flex items-center gap-3 flex-shrink-0">
                          <button
                            onClick={() => (editingId === r.id ? setEditingId(null) : startEdit(r))}
                            className="text-[12.5px] font-bold text-pb hover:text-navy transition"
                          >
                            {editingId === r.id ? 'Close' : 'Edit'}
                          </button>
                          <button
                            onClick={() => handleDelete(r)}
                            disabled={deletingId === r.id}
                            className="text-[12.5px] font-bold text-dr/60 hover:text-dr transition disabled:opacity-50"
                          >
                            Remove
                          </button>
                        </div>
                      </div>

                      {editingId === r.id && (
                        <div className="bg-surface px-[18px] py-5 border-t border-[#F1F3F6]">
                          <form onSubmit={handleUpdate} className="space-y-5">
                            <ResourceFields state={editForm} setState={setEditForm} />

                            {/* File management */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                              {['pdf', 'docx'].map((format) => {
                                const label = format === 'pdf' ? 'PDF version' : 'Word version';
                                const url = r[`${format}Url`];
                                const name = r[`${format}Name`];
                                const size = r[`${format}Size`];
                                const busy = uploadingKey === `${r.id}-${format}`;
                                return (
                                  <div key={format} className="bg-white border border-[#E5E7EB] rounded-[10px] p-3">
                                    <p className="text-[11px] font-extrabold text-[#9CA3AF] uppercase tracking-[0.7px] mb-2">{label}</p>
                                    {url ? (
                                      <div className="flex items-center justify-between gap-2 mb-2">
                                        <a href={url} target="_blank" rel="noopener noreferrer"
                                          className="text-[12px] font-semibold text-pb hover:text-navy truncate">
                                          {name || 'Download'} {size ? `(${formatBytes(size)})` : ''}
                                        </a>
                                        <button type="button" onClick={() => handleRemoveFile(r, format)}
                                          className="text-[11.5px] font-bold text-dr/60 hover:text-dr flex-shrink-0">Remove</button>
                                      </div>
                                    ) : (
                                      <p className="text-[12px] text-[#9CA3AF] mb-2">Not uploaded.</p>
                                    )}
                                    <input
                                      type="file"
                                      accept={format === 'pdf' ? '.pdf,application/pdf' : '.doc,.docx'}
                                      disabled={busy}
                                      onChange={(e) => handleReplaceFile(r, format, e)}
                                      className="w-full text-[12px] text-[#6B7280] file:mr-3 file:px-3 file:py-[6px] file:rounded-[8px] file:border-0 file:text-[12px] file:font-bold file:bg-blue-light file:text-pb hover:file:bg-[#DCE7F5]"
                                    />
                                    {busy && <p className="text-[11.5px] text-pb mt-1">Uploading…</p>}
                                  </div>
                                );
                              })}
                            </div>

                            {error && <p className="text-[12px] text-dr">{error}</p>}
                            <div className="flex gap-2">
                              <button type="submit" disabled={saving}
                                className="px-[18px] py-[10px] text-[13px] font-bold text-white bg-pb hover:bg-navy rounded-[9px] transition disabled:opacity-60">
                                {saving ? 'Saving…' : 'Save changes'}
                              </button>
                              <button type="button" onClick={() => setEditingId(null)}
                                className="px-4 py-[10px] text-[13px] font-semibold text-[#6B7280] hover:text-[#374151] transition">
                                Cancel
                              </button>
                            </div>
                          </form>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
